import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    Button,
    TextField,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    useTheme,
    IconButton,
    Tooltip,
    Paper,
    Divider
} from '@mui/material';
import {
    ExitToApp,
    History,
    Edit,
    Schedule
} from '@mui/icons-material';
import { Sunrise, Sunset, Target, Bold, Italic, List, ListOrdered, Undo, Redo } from 'lucide-react';
import { Layout } from '@layout/Layout';
import useAttendanceStore from '@/store/Employee/AttendanceStore';
import useDailyPlanStore, { TaskStatus } from '@/store/Employee/DailyPlanStore';
import EodReportPanel from '@components/DailyGate/EodReportPanel';
import PlanForTheDayPanel from '@components/DailyGate/PlanForTheDayPanel';

// === TIP TAP IMPORTS ===
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';

// === RICH TEXT EDITOR COMPONENT (EMBEDDED) ===
const RichTextEditor: React.FC<{
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    disabled?: boolean;
}> = ({ value, onChange, placeholder = 'Start typing…', disabled = false }) => {
    const isInternalUpdate = React.useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Placeholder.configure({
                placeholder,
            }),
        ],
        content: value,
        editable: !disabled,
        onUpdate: ({ editor }: { editor: Editor }) => {
            isInternalUpdate.current = true;
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm focus:outline-none min-h-[150px] p-3',
            },
        },
    });

    React.useEffect(() => {
        if (editor && !isInternalUpdate.current && value !== editor.getHTML()) {
            editor.commands.setContent(value, { emitUpdate: false });
        }
        isInternalUpdate.current = false;
    }, [value, editor]);

    // TipTap only applies `editable` at creation. Keep it in sync when the
    // `disabled` prop changes (e.g. once today's attendance loads), otherwise the
    // editor stays uneditable.
    React.useEffect(() => {
        if (editor && editor.isEditable === disabled) {
            editor.setEditable(!disabled);
        }
    }, [editor, disabled]);

    if (!editor) return null;

    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                borderColor: disabled ? 'grey.300' : 'divider',
                '& .ProseMirror': {
                    minHeight: '150px',
                    padding: '12px',
                    outline: 'none',
                    '&:focus': {
                        outline: 'none',
                    },
                },
                '& .ProseMirror p.is-editor-empty:first-of-type::before': {
                    content: 'attr(data-placeholder)',
                    float: 'left',
                    color: 'text.disabled',
                    pointerEvents: 'none',
                    height: 0,
                },
            }}
        >
            {/* Toolbar */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                }}
            >
                <Tooltip title="Bold">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        disabled={disabled}
                        color={editor.isActive('bold') ? 'primary' : 'default'}
                    >
                        <Bold size={18} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Italic">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        disabled={disabled}
                        color={editor.isActive('italic') ? 'primary' : 'default'}
                    >
                        <Italic size={18} />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem />

                <Tooltip title="Bullet List">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        disabled={disabled}
                        color={editor.isActive('bulletList') ? 'primary' : 'default'}
                    >
                        <List size={18} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Numbered List">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        disabled={disabled}
                        color={editor.isActive('orderedList') ? 'primary' : 'default'}
                    >
                        <ListOrdered size={18} />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem />

                <Tooltip title="Undo">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={disabled || !editor.can().undo()}
                    >
                        <Undo size={18} />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Redo">
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={disabled || !editor.can().redo()}
                    >
                        <Redo size={18} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Editor */}
            <EditorContent
                editor={editor}
                style={{ minHeight: 150 }}
            />
        </Paper>
    );
};

const MyWork: React.FC = () => {
    const theme = useTheme();
    const [dailyUpdate, setDailyUpdate] = useState('');
    const [editRemarksDialog, setEditRemarksDialog] = useState(false);
    const [eodDialogOpen, setEodDialogOpen] = useState(false);
    const [planDialogOpen, setPlanDialogOpen] = useState(false);

    const {
        todayAttendance,
        loading,
        error,
        successMessage,
        fetchTodayAttendance,
        handleUpdateRemarks,
        resetMessages
    } = useAttendanceStore();

    const dayState = useDailyPlanStore((s) => s.dayState);
    const fetchDayState = useDailyPlanStore((s) => s.fetchDayState);

    useEffect(() => {
        fetchTodayAttendance();
        fetchDayState();
    }, []);

    useEffect(() => {
        if (todayAttendance?.remarks) {
            setDailyUpdate(todayAttendance.remarks);
        }
    }, [todayAttendance]);

    // If the plan is submitted through the app-wide gate while already on this
    // page, only DailyPlanStore updates — refetch today's attendance so the
    // Daily Note editor unlocks without a manual reload.
    useEffect(() => {
        if (dayState?.planSubmitted && !todayAttendance) {
            fetchTodayAttendance();
        }
    }, [dayState?.planSubmitted]);

    const handleEodSubmitted = async () => {
        setEodDialogOpen(false);
        // Refresh the stat cards (punch-out time / total hours) after EOD.
        await fetchTodayAttendance();
        setTimeout(() => resetMessages(), 3000);
    };

    const handlePlanSubmitted = async () => {
        setPlanDialogOpen(false);
        // dayState is refreshed by submitPlan; refetch attendance for the stat cards.
        await fetchTodayAttendance();
    };

    const handleSubmitUpdate = async () => {
        if (!dailyUpdate.trim()) {
            return;
        }
        const success = await handleUpdateRemarks(dailyUpdate);
        if (success) {
            setEditRemarksDialog(false);
            // Refetch attendance data to update UI immediately
            await fetchTodayAttendance();
            setTimeout(() => resetMessages(), 3000);
        }
    };

    const handleViewHistory = () => {
        window.location.href = '/attendance-history';
    };

    const formatTime = (dateTime: string) => {
        if (!dateTime) return '-';
        const date = new Date(dateTime);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const calculateHoursWorked = () => {
        if (!todayAttendance?.punch_in) return '0h 0m';

        const punchIn = new Date(todayAttendance.punch_in);
        const punchOut = todayAttendance.punch_out ? new Date(todayAttendance.punch_out) : new Date();

        const diff = punchOut.getTime() - punchIn.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        return `${hours}h ${minutes}m`;
    };

    const isPunchedIn = todayAttendance?.punch_in && !todayAttendance?.punch_out;
    const isPunchedOut = todayAttendance?.punch_out;

    // Indirect flow: the plan (punch-in) and EOD (punch-out) are submitted through
    // the daily gate / the EOD dialog, not manual buttons.
    const planSubmitted = !!dayState?.planSubmitted || !!todayAttendance?.punch_in;
    const eodSubmittedToday = !!dayState?.eodSubmittedToday || !!todayAttendance?.punch_out;
    const todayTasks = dayState?.todayTasks || [];
    const plannedTasks = todayTasks.filter((t) => t.status === 'planned');

    const statusLabel = (status: TaskStatus): string => {
        switch (status) {
            case 'completed': return 'Completed';
            case 'not_completed': return 'Not completed';
            case 'dropped': return 'Dropped';
            case 'carried': return 'Carried over';
            default: return 'Planned';
        }
    };
    const statusColor = (status: TaskStatus): 'success' | 'warning' | 'default' | 'info' => {
        switch (status) {
            case 'completed': return 'success';
            case 'not_completed': return 'warning';
            case 'carried': return 'info';
            default: return 'default';
        }
    };

    return (
        <Layout>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                    {/* Header with Attendance History Link */}
                    <Box sx={{ textAlign: 'center', mb: 8, position: 'relative' }}>
                        {/* Attendance History Link - Top Right */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                zIndex: 100
                            }}
                        >
                            <Button
                                variant="text"
                                startIcon={<History />}
                                onClick={handleViewHistory}
                                sx={{
                                    color: 'text.primary',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    '&:hover': {
                                        backgroundColor: 'primary.main',
                                        color: 'white'
                                    }
                                }}
                            >
                                View History
                            </Button>
                        </Box>

                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0.1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 400,
                                    height: 400,
                                    background: theme.palette.primary.main,
                                    borderRadius: '50%',
                                    filter: 'blur(80px)',
                                    animation: 'pulse 3s ease-in-out infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 0.5 },
                                        '50%': { opacity: 0.8 }
                                    }
                                }}
                            />
                        </Box>
                        <Box sx={{ position: 'relative' }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 'bold',
                                    color: theme.palette.text.primary,
                                    mb: 2,
                                    fontSize: { xs: '3rem', md: '4rem' }
                                }}
                            >
                                Attendance Portal
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        bgcolor: isPunchedIn ? 'success.main' : 'text.disabled',
                                        borderRadius: '50%',
                                        animation: isPunchedIn ? 'pulse 2s ease-in-out infinite' : 'none',
                                    }}
                                />
                                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                                    {isPunchedOut ? "Day completed successfully" : isPunchedIn ? "You're doing great today" : "Ready to start your day"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Success/Error Messages */}
                    {successMessage && (
                        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={resetMessages}>
                            {successMessage}
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={resetMessages}>
                            {error}
                        </Alert>
                    )}

                    {/* Creative Flow Design */}
                    <Box sx={{ maxWidth: 1000, mx: 'auto', mb: 6 }}>
                        {/* Main Attendance Card */}
                        <Card
                            sx={{
                                borderRadius: 4,
                                overflow: 'hidden',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                                background: theme.palette.background.paper
                            }}
                        >
                            {/* Progress Bar */}
                            <Box sx={{ position: 'relative', height: 8, bgcolor: 'grey.100' }}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        height: '100%',
                                        width: isPunchedOut ? '100%' : todayAttendance?.punch_in ? '50%' : '0%',
                                        background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.error.main} 100%)`,
                                        transition: 'width 0.8s ease-in-out'
                                    }}
                                />
                            </Box>

                            <Box sx={{ p: 4 }}>
                                {/* Status Row */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                            Today's Work Session
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {isPunchedOut ? 'Session completed' : isPunchedIn ? 'Currently active' : 'Not started yet'}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={isPunchedOut ? 'COMPLETE' : isPunchedIn ? 'ACTIVE' : 'PENDING'}
                                        sx={{
                                            bgcolor: isPunchedOut ? 'error.main' : isPunchedIn ? 'primary.main' : 'grey.300',
                                            color: isPunchedOut || isPunchedIn ? 'white' : 'text.secondary',
                                            fontWeight: 700,
                                            px: 2,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Box>

                                {/* Three Column Stats */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
                                    {/* Start Time */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            p: 3,
                                            borderRadius: 2,
                                            bgcolor: todayAttendance?.punch_in ? 'success.main' : (theme.palette.mode === 'dark' ? '#000' : 'grey.50'),
                                            color: todayAttendance?.punch_in ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'text.secondary'),
                                            transition: 'all 0.3s ease',
                                            border: `2px solid ${todayAttendance?.punch_in ? theme.palette.success.light : (theme.palette.mode === 'dark' ? '#333' : theme.palette.grey[200])}`
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Sunrise size={20} color={todayAttendance?.punch_in ? 'white' : 'currentColor'} style={{ color: todayAttendance?.punch_in ? 'white' : (theme.palette.mode === 'dark' ? 'white' : theme.palette.text.disabled) }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Start Time
                                            </Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                            {todayAttendance?.punch_in ? formatTime(todayAttendance.punch_in) : '--:--'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            {todayAttendance?.punch_in ? 'Checked in' : 'Not started'}
                                        </Typography>
                                    </Box>

                                    {/* Duration */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            p: 3,
                                            borderRadius: 2,
                                            bgcolor: todayAttendance?.punch_in ? 'primary.main' : (theme.palette.mode === 'dark' ? '#000' : 'grey.50'),
                                            color: todayAttendance?.punch_in ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'text.secondary'),
                                            transition: 'all 0.3s ease',
                                            border: `2px solid ${todayAttendance?.punch_in ? theme.palette.primary.light : (theme.palette.mode === 'dark' ? '#333' : theme.palette.grey[200])}`
                                        }}
                                    >
                                        {isPunchedIn && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 12,
                                                    right: 12,
                                                    width: 8,
                                                    height: 8,
                                                    bgcolor: 'success.light',
                                                    borderRadius: '50%',
                                                    animation: 'pulse 2s ease-in-out infinite'
                                                }}
                                            />
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Schedule sx={{ fontSize: 20, color: todayAttendance?.punch_in ? 'white' : (theme.palette.mode === 'dark' ? 'white' : theme.palette.text.disabled) }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Duration
                                            </Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                            {todayAttendance?.punch_in ? (todayAttendance.total_hours ? `${Number(todayAttendance.total_hours).toFixed(2)}h` : calculateHoursWorked()) : '--h --m'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            {isPunchedIn ? 'In progress' : todayAttendance?.punch_out ? 'Total time' : 'No activity'}
                                        </Typography>
                                    </Box>

                                    {/* End Time */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            p: 3,
                                            borderRadius: 2,
                                            bgcolor: isPunchedOut ? 'error.main' : (theme.palette.mode === 'dark' ? '#000' : 'grey.50'),
                                            color: isPunchedOut ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'text.secondary'),
                                            transition: 'all 0.3s ease',
                                            border: `2px solid ${isPunchedOut ? theme.palette.error.light : (theme.palette.mode === 'dark' ? '#333' : theme.palette.grey[200])}`
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Sunset size={20} color={isPunchedOut ? 'white' : 'currentColor'} style={{ color: isPunchedOut ? 'white' : (theme.palette.mode === 'dark' ? 'white' : theme.palette.text.disabled) }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                End Time
                                            </Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                            {isPunchedOut ? formatTime(todayAttendance.punch_out!) : '--:--'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            {isPunchedOut ? 'Checked out' : 'Pending'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Action area — punch-in/out are now indirect (plan / EOD) */}
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                    {!planSubmitted && (
                                        <Box sx={{ textAlign: 'center', py: 2 }}>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                startIcon={<Schedule />}
                                                onClick={() => setPlanDialogOpen(true)}
                                                sx={{
                                                    px: 6,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: 'success.main',
                                                    fontWeight: 600,
                                                    fontSize: '1rem',
                                                    textTransform: 'none',
                                                    boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                                                    '&:hover': {
                                                        bgcolor: 'success.dark',
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)'
                                                    }
                                                }}
                                            >
                                                Start Plan for the Day
                                            </Button>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                                Submitting your plan punches you in. During working hours this opens automatically.
                                            </Typography>
                                        </Box>
                                    )}
                                    {planSubmitted && !eodSubmittedToday && (
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ExitToApp />}
                                            onClick={() => setEodDialogOpen(true)}
                                            disabled={loading}
                                            sx={{
                                                px: 6,
                                                py: 1.5,
                                                borderRadius: 2,
                                                bgcolor: 'error.main',
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                textTransform: 'none',
                                                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                                                '&:hover': {
                                                    bgcolor: 'error.dark',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)'
                                                }
                                            }}
                                        >
                                            Submit End-of-Day Report
                                        </Button>
                                    )}
                                    {eodSubmittedToday && (
                                        <Box sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                                                ✓ Work session completed
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                Great job today! See you tomorrow.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Card>
                    </Box>

                    {/* Today's Plan - structured task list */}
                    {planSubmitted && todayTasks.length > 0 && (
                        <Box sx={{ maxWidth: 1000, mx: 'auto', mb: 6 }}>
                            <Card
                                sx={{
                                    borderRadius: 4,
                                    p: 4,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                                    background: theme.palette.background.paper
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                    <Target size={24} color={theme.palette.primary.main} />
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        Today's Plan
                                    </Typography>
                                </Box>
                                {todayTasks.map((task) => (
                                    <Box
                                        key={task.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            py: 1.5,
                                            borderBottom: `1px solid ${theme.palette.divider}`
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontWeight: 600 }}>{task.title}</Typography>
                                            {task.description && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {task.description}
                                                </Typography>
                                            )}
                                            {task.status === 'not_completed' && task.incomplete_reason && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Reason: {task.incomplete_reason}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Chip
                                            label={statusLabel(task.status)}
                                            size="small"
                                            color={statusColor(task.status)}
                                            sx={{ fontWeight: 600, flexShrink: 0 }}
                                        />
                                    </Box>
                                ))}
                            </Card>
                        </Box>
                    )}

                    {/* Daily Update Section - Full Width Below Timeline */}
                    <Box sx={{ mt: 8, maxWidth: 900, mx: 'auto' }}>
                        <Card
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.secondary.main}15 0%, ${theme.palette.background.paper} 100%)`,
                                borderRadius: 4,
                                p: 4,
                                border: `2px solid ${theme.palette.secondary.light}`,
                                boxShadow: 6
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: theme.palette.mode === 'dark' ? '#000' : 'secondary.main',
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Target size={28} color="white" />
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            Daily Note
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Share an optional overall note for today
                                        </Typography>
                                    </Box>
                                </Box>
                                {todayAttendance?.remarks && (
                                    <Tooltip title="Edit Update">
                                        <IconButton
                                            onClick={() => setEditRemarksDialog(true)}
                                            sx={{
                                                bgcolor: theme.palette.mode === 'dark' ? '#000' : 'secondary.main',
                                                color: 'white',
                                                '&:hover': {
                                                    bgcolor: theme.palette.mode === 'dark' ? '#333' : 'secondary.dark'
                                                }
                                            }}
                                        >
                                            <Edit />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {todayAttendance?.remarks ? (
                                <Box
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        bgcolor: 'background.default',
                                        border: `1px solid ${theme.palette.divider}`,
                                        minHeight: 150,
                                        '& p': {
                                            margin: '0.5em 0',
                                            lineHeight: 1.8,
                                            '&:first-of-type': { marginTop: 0 },
                                            '&:last-child': { marginBottom: 0 }
                                        },
                                        '& ul, & ol': {
                                            paddingLeft: '1.5rem',
                                            margin: '0.5em 0',
                                            '& li': {
                                                marginBottom: '0.25em'
                                            }
                                        },
                                        '& strong': {
                                            fontWeight: 700
                                        },
                                        '& em': {
                                            fontStyle: 'italic'
                                        }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: todayAttendance.remarks }}
                                />
                            ) : (
                                <Box>
                                    <RichTextEditor
                                        value={dailyUpdate}
                                        onChange={setDailyUpdate}
                                        placeholder="What did you accomplished today? Share your progress, challenges, and achievements..."
                                        disabled={!todayAttendance || loading}
                                    />
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="secondary"
                                        onClick={handleSubmitUpdate}
                                        disabled={!dailyUpdate.trim() || !todayAttendance || loading}
                                        sx={{ mt: 2, py: 2, borderRadius: 2, fontSize: '1rem' }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Save Daily Update'}
                                    </Button>
                                    {!todayAttendance && (
                                        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                            Submit your Plan for the Day first to add a note
                                        </Alert>
                                    )}
                                </Box>
                            )}
                        </Card>
                    </Box>

                    {/* Edit Remarks Dialog */}
                    <Dialog open={editRemarksDialog} onClose={() => setEditRemarksDialog(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Edit Daily Update</DialogTitle>
                        <DialogContent>
                            <RichTextEditor
                                value={dailyUpdate}
                                onChange={setDailyUpdate}
                                placeholder="Update your daily remarks..."
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setEditRemarksDialog(false)}>Cancel</Button>
                            <Button variant="contained" onClick={handleSubmitUpdate} disabled={!dailyUpdate.trim() || loading}>
                                Update
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Manual Plan for the Day (user-initiated punch-in) */}
                    {planDialogOpen && (
                        <PlanForTheDayPanel
                            open
                            onClose={() => setPlanDialogOpen(false)}
                            onSubmitted={handlePlanSubmitted}
                        />
                    )}

                    {/* End-of-Day report (user-initiated punch-out for today) */}
                    {eodDialogOpen && (
                        <EodReportPanel
                            open
                            tone="eod"
                            tasks={plannedTasks}
                            onClose={() => setEodDialogOpen(false)}
                            onSubmitted={handleEodSubmitted}
                        />
                    )}
            </Container>
        </Layout>
    );
};

export default MyWork;